import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Calendar, ChevronLeft, ChevronRight, Layers, ListTodo, Diamond, Eye, EyeOff,
  ArrowRight, Filter, GripHorizontal
} from "lucide-react";
import { toast } from "sonner";

export const GanttPage = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [ganttData, setGanttData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewStart, setViewStart] = useState(new Date());
  const [lookahead, setLookahead] = useState("28");
  const [showDeps, setShowDeps] = useState(true);
  const [dragState, setDragState] = useState(null);
  const chartRef = useRef(null);

  const DAYS_TO_SHOW = parseInt(lookahead);

  useEffect(() => { fetchProjects(); }, []);
  useEffect(() => { if (selectedProject) fetchGanttData(selectedProject); }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (res.ok) { const data = await res.json(); setProjects(data); if (data.length > 0) setSelectedProject(data[0].project_id); }
    } catch (err) { console.error("Failed:", err); }
    finally { setLoading(false); }
  };

  const fetchGanttData = async (projectId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/gantt-data`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (res.ok) setGanttData(await res.json());
    } catch (err) { console.error("Failed:", err); }
    finally { setLoading(false); }
  };

  const updateGanttItem = async (itemId, itemType, startDate, endDate) => {
    try {
      const res = await fetch(`${API}/projects/${selectedProject}/gantt-update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ item_id: itemId, item_type: itemType, start_date: startDate, end_date: endDate })
      });
      if (res.ok) {
        toast.success(language === "sv" ? "Uppdaterad!" : "Updated!");
      } else {
        toast.error(language === "sv" ? "Kunde inte uppdatera" : "Failed to update");
        fetchGanttData(selectedProject);
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
      fetchGanttData(selectedProject);
    }
  };

  const dateRange = useMemo(() => {
    const dates = [];
    const start = new Date(viewStart);
    for (let i = 0; i < DAYS_TO_SHOW; i++) { const d = new Date(start); d.setDate(start.getDate() + i); dates.push(d); }
    return dates;
  }, [viewStart, DAYS_TO_SHOW]);

  const weeks = useMemo(() => {
    const grouped = []; let cw = [];
    dateRange.forEach((date, i) => { cw.push(date); if (date.getDay() === 0 || i === dateRange.length - 1) { grouped.push([...cw]); cw = []; } });
    return grouped;
  }, [dateRange]);

  const navigateTime = (direction) => { const d = new Date(viewStart); d.setDate(viewStart.getDate() + (direction * 7)); setViewStart(d); };

  const getBarPosition = (start, end) => {
    if (!start) return null;
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : startDate;
    const vst = viewStart.getTime();
    const dayWidth = 100 / DAYS_TO_SHOW;
    const startOffset = (startDate.getTime() - vst) / (1000 * 60 * 60 * 24);
    const duration = Math.max(1, (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24) + 1);
    const left = startOffset * dayWidth;
    const width = duration * dayWidth;
    if (left > 100 || left + width < 0) return null;
    return { left: `${Math.max(0, left)}%`, width: `${Math.min(width, 100 - Math.max(0, left))}%`, leftPx: Math.max(0, left), widthPx: width };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'active': case 'in_progress': return 'bg-blue-500';
      case 'delayed': return 'bg-amber-500';
      default: return 'bg-slate-400';
    }
  };

  const getMilestoneColor = (status) => status === 'completed' ? 'text-green-500' : 'text-amber-500';

  const formatDateHeader = (date) => date.toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', { day: 'numeric' });
  const formatWeekHeader = (dates) => {
    if (!dates.length) return '';
    const month = dates[0].toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', { month: 'short' });
    return `${month} v.${getWeekNumber(dates[0])}`;
  };
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };
  const isToday = (date) => date.toDateString() === new Date().toDateString();
  const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;
  const t = (sv, en) => language === "sv" ? sv : en;

  const formatDate = (d) => d.toISOString().split("T")[0];

  const handleBarMouseDown = useCallback((e, item) => {
    if (item.type === "milestone") return;
    e.preventDefault();
    e.stopPropagation();
    const chartEl = chartRef.current;
    if (!chartEl) return;
    const chartRect = chartEl.getBoundingClientRect();
    const dayWidthPx = chartRect.width / DAYS_TO_SHOW;
    setDragState({
      itemId: item.id,
      itemType: item.type,
      startX: e.clientX,
      origStart: item.start,
      origEnd: item.end,
      dayWidthPx,
      mode: "move"
    });
  }, [DAYS_TO_SHOW]);

  const handleResizeMouseDown = useCallback((e, item, edge) => {
    e.preventDefault();
    e.stopPropagation();
    const chartEl = chartRef.current;
    if (!chartEl) return;
    const chartRect = chartEl.getBoundingClientRect();
    const dayWidthPx = chartRect.width / DAYS_TO_SHOW;
    setDragState({
      itemId: item.id,
      itemType: item.type,
      startX: e.clientX,
      origStart: item.start,
      origEnd: item.end,
      dayWidthPx,
      mode: edge
    });
  }, [DAYS_TO_SHOW]);

  useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - dragState.startX;
      const deltaDays = Math.round(deltaX / dragState.dayWidthPx);
      if (deltaDays === 0) return;

      setGanttData(prev => {
        if (!prev) return prev;
        const newItems = prev.items.map(item => {
          if (item.id !== dragState.itemId) return item;
          const origS = new Date(dragState.origStart);
          const origE = new Date(dragState.origEnd || dragState.origStart);

          if (dragState.mode === "move") {
            const newStart = new Date(origS);
            newStart.setDate(origS.getDate() + deltaDays);
            const newEnd = new Date(origE);
            newEnd.setDate(origE.getDate() + deltaDays);
            return { ...item, start: formatDate(newStart), end: formatDate(newEnd) };
          } else if (dragState.mode === "resize-left") {
            const newStart = new Date(origS);
            newStart.setDate(origS.getDate() + deltaDays);
            if (newStart >= origE) return item;
            return { ...item, start: formatDate(newStart) };
          } else if (dragState.mode === "resize-right") {
            const newEnd = new Date(origE);
            newEnd.setDate(origE.getDate() + deltaDays);
            if (newEnd <= origS) return item;
            return { ...item, end: formatDate(newEnd) };
          }
          return item;
        });
        return { ...prev, items: newItems };
      });
    };

    const handleMouseUp = (e) => {
      const deltaX = e.clientX - dragState.startX;
      const deltaDays = Math.round(deltaX / dragState.dayWidthPx);

      if (deltaDays !== 0) {
        const item = ganttData?.items?.find(i => i.id === dragState.itemId);
        if (item) {
          updateGanttItem(item.id, item.type, item.start, item.end);
        }
      }
      setDragState(null);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragState, ganttData]);

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6" data-testid="gantt-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {t("Tidplan (Gantt)", "Timeline (Gantt)")}
            </h1>
            <p className="text-slate-600 mt-1">{t("Dra aktiviteter horisontellt för att ändra datum", "Drag activities horizontally to change dates")}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="icon" onClick={() => navigateTime(-1)} data-testid="gantt-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={() => setViewStart(new Date())} data-testid="gantt-today">{t("Idag", "Today")}</Button>
            <Button variant="outline" size="icon" onClick={() => navigateTime(1)} data-testid="gantt-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-full sm:w-[300px] h-10" data-testid="gantt-project-select">
              <SelectValue placeholder={t("Välj projekt", "Select project")} />
            </SelectTrigger>
            <SelectContent>
              {projects.map(p => (<SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={lookahead} onValueChange={setLookahead}>
              <SelectTrigger className="w-[140px] h-10" data-testid="gantt-lookahead">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">{t("1 vecka", "1 week")}</SelectItem>
                <SelectItem value="14">{t("2 veckor", "2 weeks")}</SelectItem>
                <SelectItem value="21">{t("3 veckor", "3 weeks")}</SelectItem>
                <SelectItem value="28">{t("4 veckor", "4 weeks")}</SelectItem>
                <SelectItem value="56">{t("8 veckor", "8 weeks")}</SelectItem>
                <SelectItem value="90">{t("3 månader", "3 months")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button variant={showDeps ? "default" : "outline"} size="sm" onClick={() => setShowDeps(!showDeps)} data-testid="gantt-toggle-deps">
            {showDeps ? <Eye className="w-4 h-4 mr-1" /> : <EyeOff className="w-4 h-4 mr-1" />}
            {t("Kopplingar", "Links")}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : !selectedProject ? (
          <Card><CardContent className="py-12 text-center"><Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-600">{t("Välj ett projekt", "Select a project")}</p></CardContent></Card>
        ) : !ganttData?.items?.length ? (
          <Card><CardContent className="py-12 text-center"><Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-600">{t("Inga etapper eller aktiviteter att visa", "No phases or activities to display")}</p><p className="text-sm text-slate-500 mt-2">{t("Skapa etapper med datum i projektinställningarna", "Create phases with dates in project settings")}</p></CardContent></Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                {/* Timeline Header */}
                <div className="flex border-b border-slate-200">
                  <div className="w-64 flex-shrink-0 bg-slate-50 p-3 border-r border-slate-200">
                    <span className="text-sm font-semibold text-slate-600">{t("Etapp / Aktivitet", "Phase / Activity")}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex border-b border-slate-100">
                      {weeks.map((wd, i) => (
                        <div key={i} className="text-xs text-slate-500 text-center py-1 border-r border-slate-100" style={{ width: `${(wd.length / DAYS_TO_SHOW) * 100}%` }}>
                          {formatWeekHeader(wd)}
                        </div>
                      ))}
                    </div>
                    <div className="flex">
                      {dateRange.map((date, i) => (
                        <div key={i}
                          className={`text-xs text-center py-2 border-r border-slate-100 ${isToday(date) ? 'bg-primary text-white font-bold' : isWeekend(date) ? 'bg-slate-100 text-slate-400' : 'text-slate-600'}`}
                          style={{ width: `${100 / DAYS_TO_SHOW}%` }}>
                          {formatDateHeader(date)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Gantt Rows */}
                {ganttData.items.map((item) => {
                  const barPos = getBarPosition(item.start, item.end);
                  const isPhase = item.type === 'phase';
                  const isMilestone = item.type === 'milestone';
                  const isDragging = dragState?.itemId === item.id;

                  return (
                    <div key={item.id}
                      className={`flex border-b border-slate-100 hover:bg-slate-50/50 ${isPhase ? 'bg-slate-50' : ''} ${isDragging ? 'bg-blue-50/50' : ''}`}
                      data-testid={`gantt-row-${item.id}`}>
                      <div className={`w-64 flex-shrink-0 p-3 border-r border-slate-200 ${!isPhase && !isMilestone ? 'pl-8' : isMilestone ? 'pl-6' : ''}`}>
                        <div className="flex items-center gap-2">
                          {isMilestone ? <Diamond className={`w-4 h-4 ${getMilestoneColor(item.status)}`} /> :
                           isPhase ? <Layers className="w-4 h-4 text-slate-400" /> :
                           <ListTodo className="w-4 h-4 text-slate-300" />}
                          <span className={`text-sm truncate ${isPhase ? 'font-semibold' : ''} ${isMilestone ? 'font-medium italic' : ''}`}>
                            {item.name}
                          </span>
                          {item.progress > 0 && !isMilestone && (
                            <Badge variant="outline" className="text-xs ml-auto">{item.progress}%</Badge>
                          )}
                          {isMilestone && item.status === 'completed' && (
                            <Badge className="text-xs ml-auto bg-green-100 text-green-700">{t("Klar", "Done")}</Badge>
                          )}
                        </div>
                        {isDragging && item.start && (
                          <div className="text-xs text-blue-600 mt-1 pl-6">
                            {item.start} {item.end && item.end !== item.start ? `→ ${item.end}` : ""}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 relative h-12" ref={item === ganttData.items[0] ? chartRef : undefined}>
                        <div className="absolute inset-0 flex">
                          {dateRange.map((date, i) => (
                            <div key={i}
                              className={`border-r border-slate-100 ${isToday(date) ? 'bg-primary/10' : isWeekend(date) ? 'bg-slate-50' : ''}`}
                              style={{ width: `${100 / DAYS_TO_SHOW}%` }} />
                          ))}
                        </div>

                        {isMilestone && barPos ? (
                          <div className="absolute top-1 bottom-1 flex items-center z-10" style={{ left: barPos.left }}>
                            <Diamond className={`w-6 h-6 fill-current ${getMilestoneColor(item.status)}`} />
                          </div>
                        ) : barPos ? (
                          <div
                            className={`absolute top-2 bottom-2 group z-10 ${isDragging ? 'opacity-80' : ''}`}
                            style={{ left: barPos.left, width: barPos.width }}
                            data-testid={`gantt-bar-${item.id}`}
                          >
                            {/* Left resize handle */}
                            {!isPhase && (
                              <div
                                className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize z-20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center"
                                onMouseDown={(e) => handleResizeMouseDown(e, item, "resize-left")}
                                data-testid={`gantt-resize-left-${item.id}`}
                              >
                                <div className="w-1 h-4 bg-white/80 rounded-full mx-auto" />
                              </div>
                            )}

                            {/* Main bar - draggable */}
                            <div
                              className={`h-full w-full rounded ${getStatusColor(item.status)} ${isPhase ? 'opacity-60' : 'cursor-grab active:cursor-grabbing'} transition-shadow ${isDragging ? 'shadow-lg ring-2 ring-blue-300' : 'hover:shadow-md'}`}
                              onMouseDown={(e) => handleBarMouseDown(e, item)}
                            >
                              {item.progress > 0 && item.progress < 100 && (
                                <div className="h-full bg-white/30 rounded-l" style={{ width: `${item.progress}%` }} />
                              )}
                              {/* Drag hint icon */}
                              {!isPhase && parseFloat(barPos.width) > 4 && (
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-60 transition-opacity pointer-events-none">
                                  <GripHorizontal className="w-4 h-4 text-white" />
                                </div>
                              )}
                            </div>

                            {/* Right resize handle */}
                            {!isPhase && (
                              <div
                                className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize z-20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center"
                                onMouseDown={(e) => handleResizeMouseDown(e, item, "resize-right")}
                                data-testid={`gantt-resize-right-${item.id}`}
                              >
                                <div className="w-1 h-4 bg-white/80 rounded-full mx-auto" />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-xs text-slate-400">{t("Datum saknas", "No dates")}</span>
                          </div>
                        )}

                        {/* Dependency arrows */}
                        {showDeps && ganttData.dependencies?.filter(d => d.from_id === item.id).map((dep, idx) => {
                          const toItem = ganttData.items.find(i => i.id === dep.to_id);
                          if (!toItem || !barPos) return null;
                          const toPos = getBarPosition(toItem.start, toItem.end);
                          if (!toPos) return null;
                          return (
                            <div key={idx} className="absolute top-1/2 z-10" style={{ left: `calc(${barPos.left} + ${barPos.width})` }}>
                              <ArrowRight className="w-3 h-3 text-red-400" />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-slate-400" /><span className="text-slate-600">{t("Planerad", "Planned")}</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-blue-500" /><span className="text-slate-600">{t("Pågående", "In Progress")}</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-green-500" /><span className="text-slate-600">{t("Klar", "Completed")}</span></div>
          <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-amber-500" /><span className="text-slate-600">{t("Försenad", "Delayed")}</span></div>
          <div className="flex items-center gap-2"><Diamond className="w-4 h-4 text-amber-500" /><span className="text-slate-600">{t("Milstolpe", "Milestone")}</span></div>
          <div className="flex items-center gap-2"><ArrowRight className="w-4 h-4 text-red-400" /><span className="text-slate-600">{t("Koppling", "Link")}</span></div>
          <div className="flex items-center gap-2"><GripHorizontal className="w-4 h-4 text-slate-400" /><span className="text-slate-600">{t("Dra för att flytta", "Drag to move")}</span></div>
        </div>
      </div>
    </AppLayout>
  );
};
